import {
  ArrowRight,
  Bolt,
  Bot,
  Gamepad2,
  Heart,
  LoaderCircle,
  LogIn,
  Shield,
  X,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";

export function PlayPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [nicknameDialogOpen, setNicknameDialogOpen] = useState(false);
  const [nickname, setNickname] = useState("");
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const busy = auth.action !== "idle" || auth.status === "loading";

  async function startGuestGame() {
    auth.clearError();
    if (auth.account?.accountType === "anonymous") {
      navigate("/play/game");
      return;
    }

    if (await auth.signInAnonymously()) {
      navigate("/play/game");
    }
  }

  async function startGoogleGame() {
    auth.clearError();
    const account =
      auth.account?.accountType === "google" ? auth.account : await auth.signInWithGoogle();

    if (!account) {
      return;
    }
    if (!account.publicNickname) {
      setNickname("");
      setNicknameError(null);
      setNicknameDialogOpen(true);
      return;
    }

    navigate("/play/game");
  }

  async function saveNickname(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = await auth.setPublicNickname(nickname);
    if (!result.ok) {
      setNicknameError(result.message ?? "A becenevet nem sikerült elmenteni.");
      return;
    }

    setNicknameDialogOpen(false);
    navigate("/play/game");
  }

  return (
    <div className="page page--play">
      <section className="page-intro content-frame">
        <p className="eyebrow"><Gamepad2 aria-hidden="true" size={16} /> Arcade indítás</p>
        <h1>Játék indítása</h1>
        <p>Válassz játékmódot, majd indulhat a World 1-1: Tokyo Ruins.</p>
      </section>

      <section className="mission-preview" aria-labelledby="mission-preview-title">
        <div className="mission-preview__image" aria-hidden="true" />
        <div className="mission-preview__body content-frame">
          <div>
            <p className="section-kicker">Aktuális küldetés</p>
            <h2 id="mission-preview-title">World 1-1: Tokyo Ruins</h2>
          </div>
          <div className="mission-stats" aria-label="Kezdő játékadatok">
            <span><Heart aria-hidden="true" size={18} /> 6 élet</span>
            <span><Bolt aria-hidden="true" size={18} /> 100% energia</span>
            <span><Shield aria-hidden="true" size={18} /> 5 lövés / élet</span>
          </div>
        </div>
      </section>

      <section className="section content-frame" aria-labelledby="mode-title">
        <div className="section-heading">
          <div>
            <p className="section-kicker">Játékmód</p>
            <h2 id="mode-title">Hogyan szeretnél játszani?</h2>
          </div>
        </div>

        <div className="mode-grid">
          <article className="mode-card mode-card--guest">
            <div className="mode-card__icon"><Bot aria-hidden="true" size={26} /></div>
            <p className="mode-card__label">Gyors indítás</p>
            <h3>Játék vendégként</h3>
            <p>
              Nincs szükség regisztrációra. A Firebase anonim játékosazonosítót és stabil, vicces
              vendégnevet készít neked.
            </p>
            <button
              className="button button--primary"
              type="button"
              disabled={busy}
              onClick={() => void startGuestGame()}
            >
              {auth.action === "anonymous" ? (
                <><LoaderCircle className="spin" aria-hidden="true" size={19} /> Indítás...</>
              ) : (
                <>Vendég játék <ArrowRight aria-hidden="true" size={19} /></>
              )}
            </button>
          </article>

          <article className="mode-card mode-card--google">
            <div className="mode-card__topline">
              <div className="mode-card__icon"><LogIn aria-hidden="true" size={26} /></div>
              <span className="status-chip">Firebase Auth</span>
            </div>
            <p className="mode-card__label">Saját játékosprofil</p>
            <h3>Belépés Google-fiókkal</h3>
            <p>
              Válassz külön nyilvános becenevet, őrizd meg a legjobb és legutóbbi eredményedet.
              Google-neved és profilképed nem kerül a toplistára.
            </p>
            <button
              className="button button--secondary"
              type="button"
              disabled={busy}
              onClick={() => void startGoogleGame()}
            >
              {auth.action === "google" ? (
                <><LoaderCircle className="spin" aria-hidden="true" size={19} /> Belépés...</>
              ) : auth.account?.accountType === "google" ? (
                <>Játék indítása <ArrowRight aria-hidden="true" size={19} /></>
              ) : (
                <><LogIn aria-hidden="true" size={19} /> Belépés Google-fiókkal</>
              )}
            </button>
          </article>
        </div>

        {auth.error && <p className="form-message form-message--error" role="alert">{auth.error}</p>}
      </section>

      <section className="launch-note content-frame" aria-label="Fejlesztési előnézet">
        <Bolt aria-hidden="true" size={20} />
        <p>
          {auth.status === "unconfigured"
            ? "A felület elkészült, de ehhez a buildhez még nincs Firebase fejlesztői konfiguráció megadva."
            : "Az anonim és a Google-fiók külön marad. A profil és a befejezett játék eredménye tartósan menthető."}
        </p>
      </section>

      {nicknameDialogOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setNicknameDialogOpen(false)}>
          <section
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="nickname-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="icon-button dialog__close" onClick={() => setNicknameDialogOpen(false)} aria-label="Bezárás">
              <X aria-hidden="true" size={20} />
            </button>
            <LogIn className="dialog__icon" aria-hidden="true" size={34} />
            <h2 id="nickname-dialog-title">Válassz játékosnevet</h2>
            <p>
              Ez a név lesz nyilvános a játékban. A Google-neved, e-mail-címed és profilképed nem
              jelenik meg a toplistán.
            </p>
            <form className="nickname-form" onSubmit={(event) => void saveNickname(event)}>
              <label htmlFor="public-nickname">Nyilvános becenév</label>
              <input
                id="public-nickname"
                name="publicNickname"
                value={nickname}
                minLength={3}
                maxLength={20}
                autoComplete="off"
                autoFocus
                onChange={(event) => {
                  setNickname(event.target.value);
                  setNicknameError(null);
                }}
              />
              <small>3-20 karakter. A név egyediségét az adatbázis ellenőrzi.</small>
              {nicknameError && <p className="form-message form-message--error" role="alert">{nicknameError}</p>}
              <button className="button button--primary" type="submit" disabled={busy}>
                {auth.action === "nickname" ? (
                  <><LoaderCircle className="spin" aria-hidden="true" size={19} /> Mentés...</>
                ) : (
                  "Becenév mentése és játék"
                )}
              </button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
